import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { quickstartFor, linkForTask } from "@/lib/quickstart";
import { visibleFor } from "@/lib/helpContent";
import { HelpCircle, MapPin, ArrowRight, ExternalLink } from "lucide-react";

// Keywords that make a workflow/FAQ relevant to a given page.
const PAGE_KW = {
  "/placements": ["placement", "request", "facility", "re-rout", "decline", "assign", "clear"],
  "/templates": ["paperwork", "packet", "template"],
  "/touchpoints": ["touchpoint", "lunch", "gift"],
  "/users": ["user", "invite", "password", "impersonat", "role"],
  "/facilities": ["facility", "capacity", "clinical instructor", "welcome"],
  "/clinical-instructors": ["clinical instructor", "bio"],
  "/students": ["student", "clearance", "paperwork", "recruit"],
  "/engagement": ["event", "engagement", "lecture"],
  "/videos": ["video"],
  "/settings": ["settings", "escalation", "digest", "sharepoint", "sync"],
  "/recruiting": ["recruit", "pipeline", "convert"],
  "/my-bio": ["bio"],
  "/schools": ["school", "affiliation", "program"],
  "/analytics": ["analytic", "convert", "report"],
  "/calendar": ["calendar", "event", "ics"],
  "/notifications": ["notification", "alert", "digest"],
  "/open-requests": ["open request", "unplaced", "claim"],
  "/dashboard": ["dashboard", "your people", "bio", "task", "checklist", "meeting"],
  "/lists": ["list", "option", "dropdown", "type", "question"],
  "/student-tasks": ["task", "checklist", "meeting", "welcome"],
};

const PAGE_TITLE = {
  "/placements": "Placement Requests", "/templates": "Templates", "/touchpoints": "Touchpoints",
  "/users": "Users", "/facilities": "Facility Profile", "/clinical-instructors": "Clinical Instructors",
  "/students": "Students", "/engagement": "Engagement", "/videos": "Video Calls", "/settings": "Settings",
  "/recruiting": "Recruiting", "/my-bio": "My Bio", "/schools": "Schools", "/analytics": "Analytics",
  "/calendar": "Calendar", "/notifications": "Notifications", "/open-requests": "Open Requests", "/dashboard": "Dashboard", "/lists": "Lists & Options", "/student-tasks": "Student Tasks",
};

export function HelpPanel() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  // Normalize to a base route (strip any /:id).
  const base = "/" + (location.pathname.split("/")[1] || "dashboard");
  const kws = PAGE_KW[base];

  // Show a "new" dot the first time this user opens each page, until they open the help panel there.
  const seenKey = "cc_help_seen";
  const readSeen = () => { try { return JSON.parse(localStorage.getItem(seenKey) || "[]"); } catch { return []; } };
  const [isNew, setIsNew] = useState(false);
  useEffect(() => {
    if (!kws) return;
    setIsNew(!readSeen().includes(base));
  }, [base, kws]);

  const markSeen = () => {
    const seen = readSeen();
    if (!seen.includes(base)) { seen.push(base); localStorage.setItem(seenKey, JSON.stringify(seen)); }
    setIsNew(false);
  };
  const handleOpen = (v) => { setOpen(v); if (v) markSeen(); };

  if (!kws) return null; // no help mapping for this page → hide the button

  const role = user?.role;
  const quick = quickstartFor(role);
  const tasks = (quick?.tasks || []).filter((t) => linkForTask(t) === base);
  const content = visibleFor(role);
  const hit = (s) => kws.some((k) => (s || "").toLowerCase().includes(k));
  const faqs = content.faqs.filter((f) => hit(f.q) || hit(f.a) || hit(f.category)).slice(0, 6);
  const workflows = content.workflows.filter((w) => hit(w.title) || hit(w.steps.join(" "))).slice(0, 4);
  const title = PAGE_TITLE[base] || "this page";
  const nothing = tasks.length === 0 && faqs.length === 0 && workflows.length === 0;

  return (
    <>
      <button onClick={() => handleOpen(true)} data-testid="help-panel-trigger" title={`Help for ${title}`}
        className="relative p-2 rounded-md hover:bg-zinc-100 text-zinc-600 hover:text-cc-blue transition-colors">
        <HelpCircle className="w-5 h-5" />
        {isNew && (
          <span className="absolute top-1 right-1 flex h-2.5 w-2.5" data-testid="help-panel-new-dot">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cc-orange opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-cc-orange" />
          </span>
        )}
      </button>

      <Sheet open={open} onOpenChange={handleOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto cc-scroll" data-testid="help-panel">
          <SheetHeader className="text-left">
            <SheetTitle className="flex items-center gap-2"><HelpCircle className="w-5 h-5 text-cc-blue" />Help — {title}</SheetTitle>
            <SheetDescription>How to do the common tasks on this page for your role ({role}).</SheetDescription>
          </SheetHeader>

          <div className="mt-4 space-y-5">
            {nothing && <p className="text-sm text-zinc-500">No specific tips for this page yet. Open the full Help Center for everything.</p>}

            {tasks.length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-wide text-zinc-400 font-semibold mb-2">How to</p>
                <Accordion type="single" collapsible className="space-y-2">
                  {tasks.map((t, i) => (
                    <AccordionItem key={i} value={`t-${i}`} className="border rounded-md px-3" data-testid={`help-panel-task-${i}`}>
                      <AccordionTrigger className="text-left text-sm font-medium hover:no-underline py-3">{t.q}</AccordionTrigger>
                      <AccordionContent className="pb-3">
                        <p className="flex items-center gap-1.5 text-xs text-cc-blue mb-2"><MapPin className="w-3 h-3" />{t.where}</p>
                        <ol className="space-y-2">
                          {t.steps.map((s, j) => (
                            <li key={j} className="flex items-start gap-2 text-sm text-zinc-700">
                              <span className="w-4 h-4 mt-0.5 shrink-0 rounded-full bg-cc-blue text-white text-[10px] font-bold flex items-center justify-center">{j + 1}</span>
                              <span>{s}</span>
                            </li>
                          ))}
                        </ol>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            )}

            {faqs.length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-wide text-zinc-400 font-semibold mb-2">FAQs</p>
                <Accordion type="single" collapsible className="space-y-2">
                  {faqs.map((f) => (
                    <AccordionItem key={f.id} value={f.id} className="border rounded-md px-3" data-testid={`help-panel-faq-${f.id}`}>
                      <AccordionTrigger className="text-left text-sm font-medium hover:no-underline py-3">{f.q}</AccordionTrigger>
                      <AccordionContent className="pb-3 text-sm text-zinc-600">{f.a}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            )}

            {workflows.length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-wide text-zinc-400 font-semibold mb-2">Related workflows</p>
                <ul className="space-y-1.5">
                  {workflows.map((w) => <li key={w.id} className="text-sm text-zinc-700 flex items-start gap-2"><ArrowRight className="w-3.5 h-3.5 mt-1 text-cc-blue shrink-0" />{w.title}</li>)}
                </ul>
              </div>
            )}

            <button onClick={() => { setOpen(false); navigate("/help"); }} data-testid="help-panel-open-full"
              className="w-full flex items-center justify-center gap-1.5 text-sm font-medium text-cc-blue border border-cc-blue/30 rounded-md py-2.5 hover:bg-cc-blue/5">
              Open the full Help Center <ExternalLink className="w-4 h-4" />
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
